import React from "react";
import {
  AttachFile,
  DateRange,
  DirectionsCarFilledOutlined,
  HowToReg,
  InfoOutlined,
  InsertChart,
  LocalGasStationOutlined,
  PeopleOutline,
  SocialDistance,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";

import { useVehicleFuelDetailContext } from "contexts/VehicleFuelDetailContext";
import FileIcon from "components/Shared/FileIcon";
import { localizeDateShort, localizeDateTimeShort } from "utils/dateFormats";

const VehicleFuelBasicDetail = () => {
  const { vehicleFuelDetail, loadingVehicleFuel } =
    useVehicleFuelDetailContext();

  return !loadingVehicleFuel ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InsertChart color="primary" />}
            title="Fuel ID"
            subtitle={vehicleFuelDetail?.fuelId}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <DirectionsCarFilledOutlined color="primary" />}
            title="Vehicle"
            subtitle={
              vehicleFuelDetail?.vehicleId?.registration_number || "N/A"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <PeopleOutline color="primary" />}
            title="Merchant"
            subtitle={vehicleFuelDetail?.merchant?.merchantName || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <SocialDistance color="primary" />}
            title="Odometer"
            subtitle={`${vehicleFuelDetail.odometer} km`}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <SocialDistance color="primary" />}
            title="Distance"
            subtitle={`${vehicleFuelDetail?.distance} km`}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRange color="primary" />}
            title="Date"
            subtitle={localizeDateShort(vehicleFuelDetail?.createdAt)}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Total Cost"
            subtitle={
              vehicleFuelDetail?.fuel?.length
                ? `Rs. ${vehicleFuelDetail.fuel
                    .reduce((sum, f) => sum + (f.totalCost || 0), 0)
                    .toFixed(2)}`
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRange color="primary" />}
            title="Fuel Added Date"
            subtitle={localizeDateTimeShort(vehicleFuelDetail.fuelDate)}
          />
        </Grid>

        <Grid item md={3}>
          <InformationTile
            title="Created By"
            icon={() => <HowToReg color="primary" />}
            subtitle={vehicleFuelDetail?.createdBy?.name}
          />
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Status"
          >
            <Status isActive={vehicleFuelDetail?.isActive} />
          </InformationTile>
        </Grid>

        <Grid item xs={3}>
          <InformationTile
            icon={() => <AttachFile color="primary" />}
            title="Attachments"
          >
            <div className="flex flex-wrap gap-4">
              {vehicleFuelDetail.attachments.map((file) => (
                <a
                  key={file?._id}
                  href={file?.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "var(--primary)" }}
                >
                  <FileIcon fileName={file?.name} fileUrl={file?.url} />
                </a>
              ))}
            </div>
          </InformationTile>
        </Grid>

        <Grid item xs={12}>
          <InformationTile
            icon={() => <LocalGasStationOutlined color="primary" />}
            title="Fuel Details"
            subtitle={
              vehicleFuelDetail?.fuel?.length > 0 ? (
                <Table size="small" aria-label="fuel details">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="h7">Fuel Type</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h7">Price per Unit</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h7">Quantity</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h7">Total Cost</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicleFuelDetail.fuel.map((fuel, index) => (
                      <TableRow key={index}>
                        <TableCell>{fuel.fuelType || "N/A"}</TableCell>
                        <TableCell>
                          {fuel.price != null
                            ? `Rs. ${Number(fuel.price).toFixed(2)}`
                            : "N/A"}
                        </TableCell>
                        <TableCell>{fuel.quantity || "N/A"}</TableCell>
                        <TableCell>
                          {fuel.totalCost != null
                            ? `Rs. ${Number(fuel.totalCost).toFixed(2)}`
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                "No fuel details"
              )
            }
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default VehicleFuelBasicDetail;
